import { useLazyQuery, useMutation } from "@apollo/client";
import {
  DELETE_ALL_NOTIFICATIONS,
  MARK_ALL_NOTIFICATIONS_AS_READ,
  MARK_NOTIFICATION_AS_READ,
  MARK_NOTIFICATIONS_AS_SEEN,
} from "graphql/mutations/notifications";
import { GET_USER } from "graphql/queries";
import { GET_NOTIFICATIONS } from "graphql/queries/notifications";
import { useCallback } from "react";
import type { Notification } from "../types";

type FetchNotificationsResponse = {
  count: number;
  list: Notification[];
};

type Pagination = {
  limit: number;
  offset: number;
};

export const useFetchNotifications = () => {
  const [query] = useLazyQuery(GET_NOTIFICATIONS);
  const [markAsSeen] = useMutation(MARK_NOTIFICATIONS_AS_SEEN, {
    refetchQueries: [{ query: GET_USER }],
  });

  const fetchNotifications = useCallback(
    async (pagination: Pagination): Promise<FetchNotificationsResponse> => {
      const result = await query({
        variables: { pagination },
        fetchPolicy: "network-only",
      });

      const list = result.data?.notifications?.list ?? [];
      const count = result.data?.notifications?.count ?? 0;

      if (pagination.offset > 0) {
        const ids = list.map((n) => n.id);
        if (ids.length > 0) {
          await markAsSeen({ variables: { input: { notificationIds: ids } } });
        }
      }

      return { count, list };
    },
    [query, markAsSeen],
  );

  return { fetchNotifications };
};

export const useMarkNotificationsAsSeen = () => {
  const [mutation] = useMutation(MARK_NOTIFICATIONS_AS_SEEN, {
    refetchQueries: [{ query: GET_USER }],
  });

  const markNotificationsAsSeen = useCallback(
    (notificationIds: string[]) =>
      mutation({ variables: { input: { notificationIds } } }),
    [mutation],
  );

  return { markNotificationsAsSeen };
};

export const useMarkAsRead = () => {
  const [mutation] = useMutation(MARK_NOTIFICATION_AS_READ);

  const markAsReadMutation = useCallback(
    (notificationId: string) =>
      mutation({ variables: { input: { notificationId } } }),
    [mutation],
  );

  return { markAsReadMutation };
};

export const useMarkAllAsRead = () => {
  const [mutation] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ);

  const markAllAsReadMutation = useCallback(
    () => mutation({ variables: { input: {} } }),
    [mutation],
  );

  return { markAllAsReadMutation };
};

export const useDeleteAllNotifications = () => {
  const [mutation] = useMutation(DELETE_ALL_NOTIFICATIONS);

  const deleteAllMutation = useCallback(
    () => mutation({ variables: {} }),
    [mutation],
  );

  return { deleteAllMutation };
};
